/**
 * Endereços titulados de Lead e Cliente — regras do lado da tela (ADR-0071).
 *
 * Espelha `core/enderecos.py` do backend: mesmas 27 UFs, mesmos tamanhos de
 * coluna, mesmas mensagens e a mesma ordem de verificação. A tela valida
 * antes de enviar para mostrar o erro no campo certo sem esperar o 400 — o
 * backend continua sendo a regra final. Se uma regra mudar lá, mude aqui no
 * mesmo trabalho (os testes de `endereco.test.js` citam cada uma).
 *
 * Tudo aqui é puro: nenhuma função altera a lista ou o endereço recebido.
 *
 * Formato de um endereço (o mesmo do payload `enderecos[]` da API):
 *   { id?, titulo, cep, logradouro, numero, complemento, bairro, cidade,
 *     estado, cobranca? (só cliente) }
 * A posição na lista é a ordem; o primeiro é o principal.
 */

export const UFS = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
];

const SIGLAS_UF = new Set(UFS.map((uf) => uf.sigla));

/** Tamanho máximo de cada coluna — os mesmos de `core.enderecos.LIMITES`. */
export const LIMITES = {
  titulo: 60,
  cep: 12,
  logradouro: 200,
  numero: 20,
  complemento: 80,
  bairro: 80,
  cidade: 120,
  estado: 2,
};

export const CAMPOS_ENDERECO = [
  "cep", "logradouro", "numero", "complemento", "bairro", "cidade", "estado",
];

/** Um endereço precisa de pelo menos um destes para localizar algum lugar. */
export const CAMPOS_MINIMOS = ["cep", "logradouro", "cidade"];

export const MAX_ENDERECOS = 20;
export const CEP_DIGITOS = 8;

/** Chave do erro que é do endereço inteiro, não de um campo. */
export const ERRO_DO_ITEM = "item";

export const MSG_TETO = `Cadastre no máximo ${MAX_ENDERECOS} endereços.`;
export const MSG_TITULO_OBRIGATORIO = "Informe o título do endereço (ex.: Casa, Obra).";
export const MSG_TITULO_REPETIDO = "Já existe um endereço com este título.";
export const MSG_MINIMO = "Informe ao menos o CEP, o logradouro ou a cidade.";
export const MSG_UF = "UF inválida: use a sigla, ex.: SP.";
export const MSG_CEP = "CEP inválido: informe os 8 dígitos.";
export const MSG_COBRANCA = "Só um endereço pode ser o de cobrança.";

const msgTamanho = (limite) => `Use no máximo ${limite} caracteres.`;

// ============================================================
// Normalização e formatação
// ============================================================

/** Texto aparado com espaços repetidos colapsados (o `_texto` do backend). */
function textoAparado(valor) {
  if (valor === null || valor === undefined) return "";
  return String(valor).replace(/\s+/g, " ").trim();
}

/** Forma de comparação: sem acento, sem caixa, só letras e dígitos. */
function chaveTexto(valor) {
  return String(valor || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^0-9a-z]/g, "");
}

const UF_POR_NOME = new Map(UFS.map((uf) => [chaveTexto(uf.nome), uf.sigla]));

/**
 * Sigla da UF a partir da sigla ou do nome por extenso.
 *
 * "sp", "S.P.", "São Paulo" e "sao paulo" viram "SP". Devolve "" para vazio
 * e `null` quando não reconhece — quem chama decide entre recusar e guardar.
 */
export function normalizarUf(valor) {
  const texto = textoAparado(valor);
  if (!texto) return "";
  const chave = chaveTexto(texto);
  if (SIGLAS_UF.has(chave.toUpperCase())) return chave.toUpperCase();
  return UF_POR_NOME.get(chave) || null;
}

/** Só os dígitos do que foi digitado ou colado ("01310-100" → "01310100"). */
export function cepDigitos(valor) {
  return String(valor || "").replace(/\D/g, "");
}

const CEP_CARACTERES_ACEITOS = /^[\d\s.-]+$/;

/**
 * CEP em 8 dígitos. "" para vazio; `null` quando não é um CEP.
 *
 * Pontuação e espaço são aceitos; letra não: "01310-100 fundos" é um CEP com
 * observação colada, e aceitar esconderia o resto do texto (igual ao backend).
 */
export function normalizarCep(valor) {
  const texto = textoAparado(valor);
  if (!texto) return "";
  if (!CEP_CARACTERES_ACEITOS.test(texto)) return null;
  const digitos = cepDigitos(texto);
  return digitos.length === CEP_DIGITOS ? digitos : null;
}

/**
 * Máscara 00000-000 — aplique no `onBlur`, nunca no `onChange`.
 *
 * Só formata o que já é um CEP válido. Texto que não é CEP volta como a
 * pessoa digitou, para a validação apontar o erro em vez de a máscara
 * engolir parte do que foi escrito.
 */
export function mascaraCep(valor) {
  const cep = normalizarCep(valor);
  if (cep === null) return String(valor ?? "");
  if (!cep) return "";
  return `${cep.slice(0, 5)}-${cep.slice(5)}`;
}

/**
 * Linha única: "Rua X, 12, Apto 3 — Bairro — Cidade/UF — CEP 01310-100".
 *
 * Só o que existe entra; é a mesma linha de `linha_unica` do backend.
 */
export function formatarEndereco(endereco) {
  const ler = (campo) => textoAparado(endereco?.[campo]);
  const rua = [ler("logradouro"), ler("numero"), ler("complemento")].filter(Boolean).join(", ");
  const cidadeUf = [ler("cidade"), ler("estado")].filter(Boolean).join("/");
  const cep = ler("cep");
  const cepFormatado = normalizarCep(cep) ? mascaraCep(cep) : cep;
  return [rua, ler("bairro"), cidadeUf, cep ? `CEP ${cepFormatado}` : ""]
    .filter(Boolean)
    .join(" — ");
}

/** Endereço novo, sem nenhum campo preenchido além do título. */
export function enderecoVazio(titulo = "") {
  return {
    titulo,
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
  };
}

/** Nenhum campo de endereço preenchido (o título não conta). */
export function enderecoSemDados(endereco) {
  return CAMPOS_ENDERECO.every((campo) => !textoAparado(endereco?.[campo]));
}

// ============================================================
// Validação (as mesmas regras de core.enderecos.validar_enderecos)
// ============================================================

function adicionarErro(erros, campo, mensagem) {
  return { ...erros, [campo]: [...(erros[campo] || []), mensagem] };
}

function validarTitulo(endereco) {
  const titulo = textoAparado(endereco?.titulo);
  if (!titulo) return [MSG_TITULO_OBRIGATORIO];
  if (titulo.length > LIMITES.titulo) return [msgTamanho(LIMITES.titulo)];
  return [];
}

function erroDoCampo(campo, valor) {
  let texto = textoAparado(valor);
  if (campo === "cep") {
    texto = normalizarCep(texto);
    if (texto === null) return { erro: MSG_CEP };
  } else if (campo === "estado") {
    texto = normalizarUf(texto);
    if (texto === null) return { erro: MSG_UF };
  }
  if (texto.length > LIMITES[campo]) return { erro: msgTamanho(LIMITES[campo]) };
  return { valor: texto };
}

function inalterado(endereco, original) {
  return CAMPOS_ENDERECO.every(
    (campo) => textoAparado(endereco?.[campo]) === String(original?.[campo] ?? "")
  );
}

/**
 * Erros de UM endereço, no formato do 400 da API: `{campo: [mensagens]}`.
 *
 * `{}` quando está ok. Campo `item` = erro do endereço inteiro ("informe ao
 * menos o CEP, o logradouro ou a cidade"), que só aparece quando os campos
 * em si não têm erro — como no backend.
 *
 * `original` é o endereço como veio do servidor: se os campos voltaram
 * iguais, não são revalidados. Um legado com UF "XX" ou só com a UF (vindo
 * de formulário) não pode travar a edição do título ou dos outros endereços.
 */
export function validarEndereco(endereco, { original } = {}) {
  let erros = {};
  validarTitulo(endereco).forEach((msg) => {
    erros = adicionarErro(erros, "titulo", msg);
  });
  if (original && inalterado(endereco, original)) return erros;

  const valores = {};
  let camposComErro = false;
  CAMPOS_ENDERECO.forEach((campo) => {
    const { erro, valor } = erroDoCampo(campo, endereco?.[campo]);
    if (erro) {
      camposComErro = true;
      erros = adicionarErro(erros, campo, erro);
    } else {
      valores[campo] = valor;
    }
  });
  if (!camposComErro && !CAMPOS_MINIMOS.some((campo) => valores[campo])) {
    erros = adicionarErro(erros, ERRO_DO_ITEM, MSG_MINIMO);
  }
  return erros;
}

/**
 * Erros da lista completa, alinhados à posição de cada endereço.
 *
 * Devolve `[]` quando está tudo certo; senão uma entrada por endereço (`{}`
 * para o que está ok), ou `[MSG_TETO]` quando o problema é a lista — o mesmo
 * formato de `enderecos` no 400 da API, para a tela tratar os dois igual.
 * Opções:
 *   - `permitirCobranca`: confere "no máximo um de cobrança" (só cliente).
 *   - `originais`: endereços como vieram do servidor (casados por `id`).
 * Pertencer ao cadastro (id de outro pai) só o backend confere.
 */
export function validarEnderecos(lista, { permitirCobranca = false, originais = [] } = {}) {
  const enderecos = Array.isArray(lista) ? lista : [];
  if (enderecos.length > MAX_ENDERECOS) return [MSG_TETO];

  const originalPorId = new Map(
    (originais || []).filter((e) => e?.id).map((e) => [String(e.id), e])
  );
  const titulosVistos = new Set();
  let cobrancas = 0;
  const erros = enderecos.map((endereco) => {
    const original = endereco?.id ? originalPorId.get(String(endereco.id)) : undefined;
    let erro = validarEndereco(endereco, { original });

    const chaveTitulo = textoAparado(endereco?.titulo).toLowerCase();
    if (chaveTitulo && titulosVistos.has(chaveTitulo)) {
      erro = adicionarErro(erro, "titulo", MSG_TITULO_REPETIDO);
    }
    titulosVistos.add(chaveTitulo);

    if (permitirCobranca && endereco?.cobranca) {
      cobrancas += 1;
      if (cobrancas > 1) erro = adicionarErro(erro, "cobranca", MSG_COBRANCA);
    }
    return erro;
  });
  return erros.some((erro) => Object.keys(erro).length > 0) ? erros : [];
}

// ============================================================
// Operações na lista (puras)
// ============================================================

function indiceValido(lista, indice) {
  return Number.isInteger(indice) && indice >= 0 && indice < lista.length;
}

/** Move o endereço de `de` para `para`; os demais mantêm a ordem relativa. */
export function mover(lista, de, para) {
  if (!indiceValido(lista, de) || !indiceValido(lista, para) || de === para) {
    return lista;
  }
  const copia = [...lista];
  const [endereco] = copia.splice(de, 1);
  copia.splice(para, 0, endereco);
  return copia;
}

/** O endereço em `indice` vira o primeiro — o principal. */
export function tornarPrincipal(lista, indice) {
  return mover(lista, indice, 0);
}

export function remover(lista, indice) {
  if (!indiceValido(lista, indice)) return lista;
  return lista.filter((_, posicao) => posicao !== indice);
}

/** Troca o endereço em `indice` por `endereco`. */
export function substituir(lista, indice, endereco) {
  if (!indiceValido(lista, indice)) return lista;
  return lista.map((atual, posicao) => (posicao === indice ? endereco : atual));
}

/**
 * Marca (ou desmarca) o endereço de cobrança. Marcar um desmarca os outros:
 * o backend recusa dois (UniqueConstraint parcial).
 */
export function marcarCobranca(lista, indice, marcado) {
  if (!indiceValido(lista, indice)) return lista;
  return lista.map((endereco, posicao) => {
    const cobranca = posicao === indice
      ? Boolean(marcado)
      : Boolean(endereco.cobranca) && !marcado;
    return endereco.cobranca === cobranca ? endereco : { ...endereco, cobranca };
  });
}

/**
 * Posição do endereço que a cobrança usa: o marcado; sem marcado, o
 * principal (decisão do dono, 2026-09-15). -1 para lista vazia.
 */
export function indiceCobranca(lista) {
  if (!Array.isArray(lista) || lista.length === 0) return -1;
  const marcado = lista.findIndex((endereco) => endereco?.cobranca);
  return marcado === -1 ? 0 : marcado;
}

/**
 * Lista pronta para `enderecos` no POST/PATCH: tira o que é só da tela
 * (`_chave`) e o que o backend calcula (`principal`, `ordem`, `linha_unica`).
 * `cobranca` só vai quando `permitirCobranca` (Lead não tem cobrança).
 */
export function enderecosParaPayload(lista, { permitirCobranca = false } = {}) {
  return (Array.isArray(lista) ? lista : []).map((endereco) => {
    const item = { titulo: endereco.titulo ?? "" };
    if (endereco.id) item.id = endereco.id;
    CAMPOS_ENDERECO.forEach((campo) => {
      item[campo] = endereco[campo] ?? "";
    });
    if (permitirCobranca) item.cobranca = Boolean(endereco.cobranca);
    return item;
  });
}

// ============================================================
// Cartões do editor x lista enviada (a mesma regra no Lead e no Cliente)
// ============================================================

/**
 * Cartão novo que ninguém preencheu: sem id, sem título e sem nenhum campo.
 *
 * Não vai ao backend — senão o cartão inicial do Novo Lead, ou um "Adicionar
 * endereço" clicado sem querer, viraria erro de título obrigatório e travaria
 * o salvar. Endereço gravado (com id) esvaziado continua indo: apagar é pelo
 * botão "Remover".
 */
export function cartaoEmBranco(endereco) {
  return !endereco?.id && !textoAparado(endereco?.titulo) && enderecoSemDados(endereco);
}

/**
 * O que vai em `enderecos` no POST/PATCH e de qual cartão veio cada item.
 *
 * `posicoes[k]` é a posição, no editor, do k-ésimo endereço enviado — o 400
 * da API aponta o erro pelo índice do que foi ENVIADO.
 */
export function prepararEnderecos(lista, { permitirCobranca = false } = {}) {
  const cartoes = Array.isArray(lista) ? lista : [];
  const posicoes = [];
  cartoes.forEach((endereco, posicao) => {
    if (!cartaoEmBranco(endereco)) posicoes.push(posicao);
  });
  return {
    enderecos: enderecosParaPayload(
      posicoes.map((posicao) => cartoes[posicao]),
      { permitirCobranca }
    ),
    posicoes,
  };
}

/**
 * Erros alinhados à lista ENVIADA (400 da API ou `validarEnderecos`)
 * reposicionados nos cartões do editor. Erro da lista inteira (strings, ex.:
 * teto de endereços) passa como veio. `[]` quando não há erro.
 */
export function errosNosCartoes(erros, posicoes, totalDeCartoes) {
  if (!Array.isArray(erros) || erros.length === 0) return [];
  if (erros.every((erro) => typeof erro === "string")) return erros;
  const porCartao = Array.from({ length: totalDeCartoes }, () => ({}));
  erros.forEach((erro, indiceEnviado) => {
    const posicao = posicoes[indiceEnviado];
    if (posicao !== undefined && erro && typeof erro === "object") porCartao[posicao] = erro;
  });
  return porCartao;
}

/**
 * Validação antes de enviar, só dos cartões que vão ao backend: `[]` quando
 * pode enviar; senão erros por cartão (o formato da prop `erros` do editor).
 * Opções como em `validarEnderecos`.
 */
export function validarCartoes(lista, { permitirCobranca = false, originais = [] } = {}) {
  const cartoes = Array.isArray(lista) ? lista : [];
  const { posicoes } = prepararEnderecos(cartoes);
  const enviados = posicoes.map((posicao) => cartoes[posicao]);
  return errosNosCartoes(
    validarEnderecos(enviados, { permitirCobranca, originais }),
    posicoes,
    cartoes.length
  );
}

/**
 * Erros de endereço de uma falha da API (`err.payload.enderecos` do 400), já
 * nos cartões de `lista` — a mesma lista que foi enviada. `null` quando a
 * falha não é de endereço.
 */
export function errosDaRespostaNosCartoes(err, lista) {
  const erros = err?.payload?.enderecos;
  if (!Array.isArray(erros) || erros.length === 0) return null;
  const cartoes = Array.isArray(lista) ? lista : [];
  return errosNosCartoes(erros, prepararEnderecos(cartoes).posicoes, cartoes.length);
}

// ============================================================
// Busca de CEP ao lado do campo (EnderecoFields e FormRenderer)
// ============================================================

/** Estado da busca de CEP de um campo. */
export const ESTADO_BUSCA_CEP = {
  OCIOSA: "ociosa",
  BUSCANDO: "buscando",
  NAO_ENCONTRADO: "nao_encontrado",
  INDISPONIVEL: "indisponivel",
};

/**
 * O que aparece ao lado do CEP em cada estado da busca — o mesmo texto para
 * quem preenche um endereço no ERP e para quem responde um formulário. Sem
 * entrada para OCIOSA: não há o que dizer. `tom` é o estado da cor.
 */
export const AVISOS_DA_BUSCA_DE_CEP = {
  [ESTADO_BUSCA_CEP.BUSCANDO]: { texto: "Buscando CEP...", tom: undefined },
  [ESTADO_BUSCA_CEP.NAO_ENCONTRADO]: {
    texto: "CEP não encontrado — preencha o endereço",
    tom: "warning",
  },
  [ESTADO_BUSCA_CEP.INDISPONIVEL]: {
    texto: "Busca de CEP indisponível — preencha o endereço",
    tom: "warning",
  },
};
